'use client';

import * as React from 'react';
import {
  ExpertBio,
  type ExpertBioHighlight,
  type ExpertBioProps,
} from '@/hen-ui/composites/ExpertBio';
import { Grid } from '@/hen-ui/composites/Grid';
import { Flex } from '@/hen-ui/composites/Flex';
import { Button } from '@/hen-ui/atomics/Button';
import { Link } from '@/hen-ui/atomics/Link';
import type { GridVariantProps } from '@/hen-ui/composites/Grid';
import type { ExpertBioContainerVariantProps } from '@/hen-ui/composites/ExpertBio';

const DEFAULT_PAGE_SIZE = 9;

export type ExpertRowItem = {
  id: string;
  imageSrc?: string;
  imageAlt?: string;
  name: string;
  /** Displayed as occupation — typically roles joined with " / " */
  occupation?: string;
  uri?: string;
  /**
   * When set, the entire card becomes a link to this URL.
   * `brandLinkLabel` is ignored for this item to avoid nested anchors.
   */
  href?: string;
  highlight?: ExpertBioHighlight;
};

export interface ExpertRowProps {
  /** Array of expert data to render */
  experts: ExpertRowItem[];
  /** ExpertBio card size (lg = vertical, sm = horizontal) */
  size?: ExpertBioContainerVariantProps['size'];
  /** ExpertBio card border styling */
  border?: ExpertBioContainerVariantProps['border'];
  /** Label for the CTA link on each card */
  brandLinkLabel?: string;
  /** Grid gap token (default: lg) */
  gap?: NonNullable<GridVariantProps['gap']>;
  /** Number of cards shown per page (default: 9) */
  pageSize?: number;
  /**
   * Document-outline role for each expert name in the row.
   * Use `section` when items are primary content under a page-level heading.
   */
  headingContext?: ExpertBioProps['headingContext'];
}

/**
 * ExpertRow — a paginated grid of ExpertBio cards.
 *
 * Renders experts in a responsive 1→2→3 column grid with a "Load More" button
 * that reveals `pageSize` additional cards on each click.
 *
 * @example
 * <ExpertRow
 *   experts={experts}
 *   size="sm"
 *   border="none"
 *   brandLinkLabel="View profile"
 * />
 *
 * @example Page section list (page h1 + item names as h2)
 * <ExpertRow experts={experts} headingContext="section" />
 */
export const ExpertRow: React.FC<ExpertRowProps> = ({
  experts,
  size = 'sm',
  border = 'none',
  brandLinkLabel,
  gap = 'lg',
  pageSize = DEFAULT_PAGE_SIZE,
  headingContext = 'default',
}) => {
  const [visibleCount, setVisibleCount] = React.useState(
    Math.min(pageSize, experts.length),
  );

  React.useEffect(() => {
    setVisibleCount(Math.min(pageSize, experts.length));
  }, [experts.length, pageSize]);

  const visible = experts.slice(0, visibleCount);
  const hasMore = visibleCount < experts.length;

  if (!experts.length) return null;

  return (
    <div className='flex flex-col items-center gap-xl'>
      <Grid columns={1} columns-md={2} columns-lg={3} gap={gap}>
        {visible.map((expert) => {
          if (!expert.name) return null;
          const brandLink =
            !expert.href && brandLinkLabel && expert.uri
              ? { href: expert.uri, label: brandLinkLabel }
              : undefined;
          const card = (
            <ExpertBio
              imgSrc={expert.imageSrc}
              imgAlt={expert.imageAlt ?? expert.name}
              name={expert.name}
              occupation={expert.occupation}
              brandLink={brandLink}
              highlight={expert.highlight}
              size={size}
              border={border}
              headingContext={headingContext}
            />
          );
          return expert.href ? (
            <Link
              key={expert.id}
              href={expert.href}
              underline='none'
              className='block'
            >
              {card}
            </Link>
          ) : (
            <React.Fragment key={expert.id}>{card}</React.Fragment>
          );
        })}
      </Grid>

      {hasMore && (
        <Flex
          direction='row'
          justify='start'
          className='pt-xxs lg:justify-center'
        >
          <Button
            type='button'
            variant='blackOutline'
            size='lg'
            className='rounded-full px-xl'
            iconAfter='arrow-down'
            onClick={() =>
              setVisibleCount((prev) =>
                Math.min(prev + pageSize, experts.length),
              )
            }
          >
            Load More
          </Button>
        </Flex>
      )}
    </div>
  );
};

ExpertRow.displayName = 'ExpertRow';
